import { ProductFormData } from '../schemas/productSchemas';
import { CreateProductData, ProductDetailsData, UpdateProductData } from '../types/types';
import { CreateProductFormData } from '../components/modals/newProductsModals/NewProductsModal';

// Remove all mapping functions as we're now using Direct API Types
// Keep only utility functions for date and number formatting

// Helper function to safely format dates
export const formatDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

// Helper function to safely convert numbers to strings
export const safeNumberToString = (value: number | null | undefined, defaultValue: string = '0'): string => {
  return value?.toString() || defaultValue;
};

// Helper function to safely convert strings to numbers
export const safeStringToNumber = (value: string | null | undefined, defaultValue: number = 0): number => {
  if (!value) return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Helper function to safely convert strings to booleans
export const safeStringToBoolean = (value: string | boolean | null | undefined, defaultValue: boolean = false): boolean => {
  if (typeof value === 'boolean') return value;
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
};

/**
 * Transforms flat Redux ProductDetailsData to nested ProductFormData structure
 * This function ensures type safety and proper mapping between the two data structures
 */
export const transformProductDataToFormData = (selectedProduct: ProductDetailsData | null): ProductFormData => {
  if (!selectedProduct) {
    return {
      productProfile: {
        name: '',
        productCode: '',
        productId: 0,
        parentProductName: '',
        type: '',
        category: '',
        subType: '',
        subCategory: '',
        status: '',
        uom: '',
        barcode: '',
        description: '',
        longDescription: '',
        standardPrice: 0,
        marginType: '',
        vendorId: undefined,
        vendorName: '',
        venProdCode: '',
        manufacturerId: undefined,
        manufacturerName: '',
        manuProductCode: '',
        activeFlag: false,
        orderable: false,
        taxable: false,
        allocationOnly: false,
        capacity: '',
        startDate: '',
        endDate: '',
        nextAvlDate: '',
        rowId: undefined,
        createdBy: undefined,
        updatedBy: undefined,
      },
      stockControl: {
        available: 0,
        inWh: 0,
        allocated: 0,
        pending: 0,
        backOrder: 0,
        onOrder: 0,
      },
      replenishment: {
        waterLevel: 0,
        reorderQuantity: 0,
        orderQtyMultiple: 0,
      },
      glAccounts: {
        glAssetAcntName: '',
        glExpAcntName: '',
        glIncAcntName: '',
        glFinanceCatName: '',
        glAssetAcntId: undefined,
        glExpAcntId: undefined,
        glIncAcntId: undefined,
      },
      extraDetails: {
        extraDetail: '',
      },
      auditProfile: {
        ownerLocId: undefined,
        ownerLocName: '',
        created: '',
        createdName: '',
        updated: '',
        updatedName: '',
      },
    };
  }

  return {
    productProfile: {
      // Core product fields
      rowId: selectedProduct.rowId,
      name: selectedProduct.name || '',
      productId: selectedProduct.productId || 0,
      productCode: selectedProduct.productCode || '',
      parentProductName: selectedProduct.parentProductName || '',
      // Vendor fields
      vendorId: selectedProduct.vendorId,
      vendorName: selectedProduct.vendorName || '',
      venProdCode: selectedProduct.venProdCode || '',
      
      // Product classification
      type: selectedProduct.type || '',
      subType: selectedProduct.subType || '',
      category: selectedProduct.category || '',
      subCategory: selectedProduct.subCategory || '',
      
      // Dates
      startDate: selectedProduct.startDate || '',
      endDate: selectedProduct.endDate || '',
      nextAvlDate: selectedProduct.nextAvlDate || '',
      
      // Pricing and financial
      standardPrice: selectedProduct.standardPrice || 0,
      marginType: selectedProduct.marginType || '',
      
      // Product details
      barcode: selectedProduct.barcode || '',
      status: selectedProduct.status || '',
      uom: selectedProduct.uom || '',
      capacity: selectedProduct.capacity || '',
      
      // Descriptions
      description: selectedProduct.description || '',
      longDescription: selectedProduct.longDescription || '',
      
      // Flags
      activeFlag: selectedProduct.activeFlag || false,
      allocationOnly: selectedProduct.allocationOnly || false,
      orderable: selectedProduct.orderable || false,
      taxable: selectedProduct.taxable || false,
      
      // Manufacturer
      manufacturerId: selectedProduct.manufacturerId,
      manufacturerName: selectedProduct.manufacturerName || '',
      manuProductCode: selectedProduct.manuProductCode || '',
      
      // Audit fields
      createdBy: selectedProduct.createdBy,
      updatedBy: selectedProduct.updatedBy,
    },
    stockControl: {
      available: selectedProduct.available || 0,
      inWh: selectedProduct.inWh || 0,
      allocated: selectedProduct.allocated || 0,
      pending: selectedProduct.pending || 0,
      backOrder: selectedProduct.backOrder || 0,
      onOrder: selectedProduct.onOrder || 0,
    },
    replenishment: {
      waterLevel: selectedProduct.waterLevel || 0,
      reorderQuantity: selectedProduct.reorderQuantity || 0,
      orderQtyMultiple: selectedProduct.orderQtyMultiple || 0,
    },
    glAccounts: {
      glAssetAcntName: selectedProduct.glAssetAcntName || '',
      glExpAcntName: selectedProduct.glExpAcntName || '',
      glIncAcntName: selectedProduct.glIncAcntName || '',
      glFinanceCatName: selectedProduct.glFinanceCatName || '',
      glAssetAcntId: selectedProduct.glAssetAcntId,
      glExpAcntId: selectedProduct.glExpAcntId,
      glIncAcntId: selectedProduct.glIncAcntId,
    },
    extraDetails: {
      extraDetail: selectedProduct.extraDetail || '',
    },
    auditProfile: {
      ownerLocId: selectedProduct.ownerLocId,
      ownerLocName: selectedProduct.ownerLocName || '',
      created: selectedProduct.created || '',
      createdName: selectedProduct.createdName || '',
      updated: selectedProduct.updated || '',
      updatedName: selectedProduct.updatedName || '',
    },
  };
};

/**
 * Transforms nested ProductFormData back to flat structure for API submission
 * This function flattens the form data to match the API expectations
 */
export const transformFormDataToProductData = (formData: ProductFormData): Partial<ProductDetailsData> => {
  const { productProfile, stockControl, replenishment, glAccounts, extraDetails, auditProfile } = formData;
  
  return {
    // Product Profile fields
    rowId: productProfile?.rowId,
    name: productProfile?.name,
    productCode: productProfile?.productCode,
    parentProductName: productProfile?.parentProductName,
    type: productProfile?.type,
    category: productProfile?.category,
    subType: productProfile?.subType,
    subCategory: productProfile?.subCategory,
    status: productProfile?.status,
    uom: productProfile?.uom,
    barcode: productProfile?.barcode,
    description: productProfile?.description,
    longDescription: productProfile?.longDescription,
    standardPrice: productProfile?.standardPrice,
    marginType: productProfile?.marginType,
    vendorId: productProfile?.vendorId,
    vendorName: productProfile?.vendorName,
    venProdCode: productProfile?.venProdCode,
    manufacturerId: productProfile?.manufacturerId,
    manufacturerName: productProfile?.manufacturerName,
    manuProductCode: productProfile?.manuProductCode,
    activeFlag: productProfile?.activeFlag,
    orderable: productProfile?.orderable,
    taxable: productProfile?.taxable,
    allocationOnly: productProfile?.allocationOnly,
    capacity: productProfile?.capacity,
    startDate: productProfile?.startDate,
    endDate: productProfile?.endDate,
    nextAvlDate: productProfile?.nextAvlDate || null,
    createdBy: productProfile?.createdBy,
    updatedBy: productProfile?.updatedBy,
    
    // Stock Control fields
    available: stockControl?.available,
    inWh: stockControl?.inWh,
    allocated: stockControl?.allocated,
    pending: stockControl?.pending,
    backOrder: stockControl?.backOrder,
    onOrder: stockControl?.onOrder,
    
    // Replenishment fields
    waterLevel: replenishment?.waterLevel,
    reorderQuantity: replenishment?.reorderQuantity,
    orderQtyMultiple: replenishment?.orderQtyMultiple,
    
    // GL Accounts fields
    glAssetAcntName: glAccounts?.glAssetAcntName,
    glExpAcntName: glAccounts?.glExpAcntName,
    glIncAcntName: glAccounts?.glIncAcntName,
    glFinanceCatName: glAccounts?.glFinanceCatName,
    glAssetAcntId: glAccounts?.glAssetAcntId,
    glExpAcntId: glAccounts?.glExpAcntId,
    glIncAcntId: glAccounts?.glIncAcntId,
    
    // Extra Details fields
    extraDetail: extraDetails?.extraDetail,
    
    // Audit Profile fields
    ownerLocId: auditProfile?.ownerLocId,
    ownerLocName: auditProfile?.ownerLocName,
    created: auditProfile?.created,
    createdName: auditProfile?.createdName,
    updated: auditProfile?.updated,
    updatedName: auditProfile?.updatedName,
  };
}; 


export const transformFormDataToUpdateProductData = (formData: ProductFormData): UpdateProductData => {
  const { productProfile, stockControl, replenishment, glAccounts, extraDetails, auditProfile } = formData;

  return {
    rowId: productProfile?.rowId || 0,
    name: productProfile?.name || '',
    productCode: productProfile?.productCode || '',
    type: productProfile?.type || '',
    category: productProfile?.category || '',
    subType: productProfile?.subType || '',
    subCategory: productProfile?.subCategory || '',
    status: productProfile?.status || '',
    uom: productProfile?.uom || '',
    barcode: productProfile?.barcode || '',
    description: productProfile?.description || '',
    longDescription: productProfile?.longDescription || '',
    standardPrice: productProfile?.standardPrice || 0,
    marginType: productProfile?.marginType || '',
    vendorId: productProfile?.vendorId || 0,
    venProdCode: productProfile?.venProdCode || '',
    manufacturerId: productProfile?.manufacturerId || 0,
    manuProductCode: productProfile?.manuProductCode || '',
    activeFlag: productProfile?.activeFlag || false,
    orderable: productProfile?.orderable || false,
    taxable: productProfile?.taxable || false,
    allocationOnly: productProfile?.allocationOnly || false,
    capacity: productProfile?.capacity || '',
    startDate: productProfile?.startDate || '',
    endDate: productProfile?.endDate || '',
    nextAvlDate: productProfile?.nextAvlDate || null || '',
    parRowId: productProfile?.parRowId || 0,
    priceListId: 0, // Default value, adjust as needed
    quantity: stockControl?.available || 0,
    onOrder: stockControl?.onOrder || 0,
    allocated: stockControl?.allocated || 0,
    inWh: stockControl?.inWh || 0,
    waterLevel: replenishment?.waterLevel || 0,
    reorderQuantity: replenishment?.reorderQuantity || 0,
    orderQtyMultiple: replenishment?.orderQtyMultiple || 0,
    location: '', // Default value, adjust as needed
    averageCost: 0, // Default value, adjust as needed
    ownerLocId: auditProfile?.ownerLocId || 0,
    glAssetAcntId: glAccounts?.glAssetAcntId || 0,
    glExpAcntId: glAccounts?.glExpAcntId || 0,
    glIncAcntId: glAccounts?.glIncAcntId || 0,
    glCategoryId: 0, // Default value, adjust as needed
    productLineId: 0, // Default value, adjust as needed
    duty: 0, // Default value, adjust as needed
  };
};


export const transformFormDataToCreateProductData = (formData: CreateProductFormData): CreateProductData => {
  const { 
    name, 
    productCode, 
    vendor, 
    vendorProductCode, 
    type, 
    subtype, 
    marginType, 
    category, 
    subcategory, 
    active, 
    orderable, 
    taxable, 
    description, 
    brand,
    brandId, 
    brandProductCode, 
    barcode, 
    orderQty, 
    purchaseExc, 
    purchaseTax, 
    purchaseInc, 
    markup, 
    sellExc, 
    sellTax, 
    sellInc, 
    nzStatus, 
    addToAll, 
    pickQuantity, 
    priceList, 
    location, 
    dangerousGoods,
    vendorId
  } = formData;

  return {
    name: name || '',
    activeFlag: active || false,
    parRowId: 0, // Default value for new product
    productCode: productCode || '',
    standardPrice: parseFloat(sellExc) || 0,
    startDate: new Date().toISOString(), // Default empty string for new product
    endDate: new Date().toISOString(), // Default empty string for new product
    nextAvlDate: null, // Default empty string for new product
    vendorId: vendorId || 0, // Will need to be resolved from vendor name
    priceListId: 0, // Default value
    productId: 0, // Default value for new product
    quantity: parseFloat(pickQuantity) || 0,
    orderable: orderable || false,
    type: type || '',
    status: nzStatus || 'Always Available',
    uom: '', // Default empty string
    venProdCode: vendorProductCode || '',
    taxable: taxable || false,
    barcode: barcode || '',
    onOrder: 0, // Default value
    allocated: 0, // Default value
    inWh: 0, // Default value
    waterLevel: 0, // Default value
    location: location ? 'Yes' : 'No', // Convert boolean to string
    category: category || '',
    allocationOnly: false, // Default value
    capacity: '', // Default empty string
    longDescription: description || '',
    averageCost: parseFloat(purchaseExc) || 0,
    subType: subtype || '',
    subCategory: subcategory || '',
    ownerLocId: 0, // Default value
    glAssetAcntId: 0, // Default value
    glExpAcntId: 0, // Default value
    glIncAcntId: 0, // Default value
    reorderQuantity: parseFloat(orderQty) || 0,
    orderQtyMultiple: 1, // Default value
    manufacturerId: brandId || 0, // Will need to be resolved from brand name
    manuProductCode: brandProductCode || '',
    marginType: marginType || '',
    description: description || '',
    glCategoryId: 0, // Default value
    dynamicStringField: '', // Default empty string
    dynamicMoneyField: 0, // Default value
    dynamicIntField: 0, // Default value
    dynamicBitField: false, // Default value
    dynamicBitField_2: false, // Default value
    productLineId: 0, // Default value
    unitPrice: parseFloat(sellInc) || 0,
    unitPurPrice: parseFloat(purchaseInc) || 0,
    priceInc: parseFloat(sellInc) || 0,
    duty: 0, // Default value
    markupPercent: parseFloat(markup) || 0,
    purchaseIncId: 0, // Default value
    purchaseTax: purchaseTax || '',
    defaultTax: sellTax || '',
    allPl: priceList || false,
    allLoc: addToAll || false,
  };
};
